import type { PvReceptionPayload } from "./payload.js";

export type PvReceptionStepKey =
  | "header"
  | "declarations"
  | "reserves"
  | "cheque"
  | "signatures"
  | "recap";

export type PvReceptionStep = {
  key: PvReceptionStepKey;
  /** Human label (FR) shown in headers and progress recaps. */
  label: string;
  /** Optional gate predicate; the step is hidden when the predicate returns false. */
  conditionalOn?: (payload: PvReceptionPayload) => boolean;
};

/**
 * Ordered list of wizard pages. The "reserves" step is only shown when the
 * team declared reservations at reception (receptionType === "avec_reserve").
 */
export const PV_RECEPTION_STEPS: readonly PvReceptionStep[] = [
  { key: "header", label: "En-tête" },
  { key: "declarations", label: "Déclarations" },
  {
    key: "reserves",
    label: "Réserves",
    conditionalOn: (p) => p.declarations?.receptionType === "avec_reserve",
  },
  { key: "cheque", label: "Chèque de solde" },
  { key: "signatures", label: "Signatures" },
  { key: "recap", label: "Récapitulatif" },
] as const;

export function findPvReceptionStep(
  key: PvReceptionStepKey,
): PvReceptionStep | undefined {
  return PV_RECEPTION_STEPS.find((step) => step.key === key);
}

export function visiblePvReceptionSteps(
  payload: PvReceptionPayload,
): PvReceptionStep[] {
  return PV_RECEPTION_STEPS.filter((step) =>
    step.conditionalOn ? step.conditionalOn(payload) : true,
  );
}

export function nextPvReceptionStep(
  current: PvReceptionStepKey,
  payload: PvReceptionPayload,
): PvReceptionStepKey | undefined {
  const visible = visiblePvReceptionSteps(payload);
  const idx = visible.findIndex((s) => s.key === current);
  if (idx === -1) return undefined;
  return visible[idx + 1]?.key;
}

export function prevPvReceptionStep(
  current: PvReceptionStepKey,
  payload: PvReceptionPayload,
): PvReceptionStepKey | undefined {
  const visible = visiblePvReceptionSteps(payload);
  const idx = visible.findIndex((s) => s.key === current);
  if (idx <= 0) return undefined;
  return visible[idx - 1]?.key;
}
